/**
 * INDICADORES DO PAINEL (VISÃO GERAL → DASHBOARD).
 *
 * Função pura: recebe os dados REAIS já lidos dos repositórios e devolve
 * todos os blocos da tela com o MESMO recorte (filtros de Projeto, Unidade /
 * Área e Período). Nenhum número é inventado — quando não há dado, o bloco
 * devolve `hasData: false` e a tela mostra o estado vazio, nunca uma curva
 * artificial.
 *
 * UM único critério de avanço para tudo (card, Curva S, Unidade e Sistema).
 * Qual critério vale depende de existir apontamento de campo no recorte:
 *
 *   COM Controle de Campo (fonte oficial, comando "Controle de Campo"):
 *     avanço = média dos percentuais das LINHAS OPERACIONAIS
 *     (Linha Fora de Operação não entra no denominador)
 *
 *   SEM apontamento de campo (comportamento histórico, preservado):
 *     avanço = inspeções concluídas ÷ inspeções do recorte
 *
 * Continua não existindo duas contas para o mesmo dado: o card "AVANÇO
 * REAL", o STATUS DAS INSPEÇÕES, os AVANÇOS POR UNIDADE e o AVANÇO POR
 * SISTEMAS saem todos da MESMA fonte escolhida — nunca misturados.
 */

export interface DashboardEquipment {
  id: string;
  clientId?: string | null;
  tag?: string | null;
  projectId?: string | null;
  unitAreaId?: string | null;
  system?: string | null;
  status?: string | null;
}

export interface DashboardInspection {
  id: string;
  clientId?: string | null;
  equipmentId?: string | null;
  projectId?: string | null;
  unitAreaId?: string | null;
  /**
   * `Inspection.inspectorId` — o Professional responsável. É o campo do
   * domínio, não `createdBy`: quem criou o registro pode ser o planejador.
   */
  inspectorProfessionalId?: string | null;
  status?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  updatedAt?: string | null;
}

export interface DashboardReport {
  id: string;
  clientId?: string | null;
  inspectionId?: string | null;
  status?: string | null;
  updatedAt?: string | null;
}

export interface DashboardRecommendation {
  id: string;
  clientId?: string | null;
  integrityReportId?: string | null;
  projectId?: string | null;
  unitAreaId?: string | null;
  equipmentId?: string | null;
  createdAt?: string | null;
}

export interface DashboardUnitArea {
  id: string;
  clientId?: string | null;
  code?: string | null;
  name?: string | null;
  projectId?: string | null;
}

export interface DashboardFilters {
  /** CLIENTE — filtra todos os componentes do Painel por Cliente autorizado. */
  clientId?: string | null;
  projectId?: string | null;
  unitAreaId?: string | null;
  from?: string | null;
  to?: string | null;
  /**
   * INSPETOR — `Professional.id`. Recorta o Painel pelo escopo individual:
   * as Linhas dos Sistemas ATRIBUÍDOS à pessoa (Controle de Campo) e as
   * inspeções em que ela é a responsável. Exige `assignments` no input para
   * o recorte de campo — sem eles, só o recorte por inspeção se aplica.
   */
  inspectorProfessionalId?: string | null;
}

/**
 * Apontamento de campo de uma LINHA (Controle de Campo). Tipo estrutural
 * local, como os demais deste módulo: o Painel não depende do domínio de
 * Controle de Campo, só do formato mínimo que precisa ler.
 */
export interface DashboardFieldProgress {
  lineEquipmentId: string;
  clientId?: string | null;
  /** Empty when the line is NOT_APPLICABLE to a piping system. */
  systemId: string | null;
  projectId?: string | null;
  unitAreaId?: string | null;
  progressPercent: number;
  outOfOperation: boolean;
  /**
   * Quando a Linha foi CONCLUÍDA. É carimbado sozinho ao chegar a 100% e
   * apagado se o percentual cair — por isso serve de base para o ritmo
   * diário, e não uma data digitada à parte que poderia discordar do
   * percentual.
   */
  completedAt?: string | null;
  startedAt?: string | null;
}

/**
 * Vínculo Inspetor → Sistema de Tubulação (`FieldInspectionAssignment`).
 * Mora aqui, e não no módulo de desempenho individual, porque o recorte por
 * Inspetor vale para o Painel inteiro — deixá-lo lá criaria dependência
 * circular entre os dois módulos.
 */
export interface DashboardAssignment {
  inspectorProfessionalId: string;
  clientId?: string | null;
  /** Empty for direct LINE assignments. */
  systemId: string | null;
  /** Directly assigned line when targetType is LINE. */
  targetType?: "SYSTEM" | "LINE";
  targetId?: string;
  active: boolean;
  projectId?: string | null;
  unitAreaId?: string | null;
}

export interface DashboardInput {
  equipments: readonly DashboardEquipment[];
  inspections: readonly DashboardInspection[];
  reports: readonly DashboardReport[];
  recommendations: readonly DashboardRecommendation[];
  unitAreas: readonly DashboardUnitArea[];
  /**
   * Avanço apontado em campo. Quando houver apontamento no recorte, ele é a
   * fonte oficial do avanço (card, status, Unidade e Sistema). Vazio =
   * comportamento histórico por contagem de inspeções.
   */
  fieldProgress?: readonly DashboardFieldProgress[];
  /** Vínculos Inspetor → Sistema, base do filtro por INSPETOR. */
  assignments?: readonly DashboardAssignment[];
  /** "Hoje" — injetado para a Curva S ser determinística em teste. */
  now?: Date;
}

export interface ProgressBar {
  id: string;
  label: string;
  percent: number;
  completed: number;
  total: number;
}

export interface CurvePoint {
  key: string;
  label: string;
  planned: number;
  real: number;
  executed: number;
}

export interface StatusSlice {
  key: "CONCLUIDAS" | "PENDENTES" | "FORA_DE_OPERACAO";
  label: string;
  count: number;
  percent: number;
}

export interface DashboardIndicators {
  assets: number;
  completedInspections: number;
  totalInspections: number;
  plannedProgress: number;
  realProgress: number;
  deviation: number;
  issuedReports: number;
  openRecommendations: number;
  curve: { hasData: boolean; points: CurvePoint[] };
  status: { hasData: boolean; total: number; slices: StatusSlice[] };
  unitProgress: { hasData: boolean; bars: ProgressBar[] };
  systemProgress: { hasData: boolean; bars: ProgressBar[] };
}

/** Inspeção concluída — estados finais do fluxo de inspeção. */
const COMPLETED_INSPECTION_STATUSES = new Set(["COMPLETED", "REPORT_ISSUED", "CLOSED", "FINISHED"]);

/**
 * REGRA V4-CAUSA2: Chave analítica para Linhas sem Sistema.
 * Existe SOMENTE no domínio analítico/ViewModel do Dashboard.
 * PROIBIDO persistir no banco ou usar como systemId real.
 */
export const OFFSITE_TUBOVIA_GROUP_ID = "__OFFSITE_TUBOVIA__";
export const OFFSITE_TUBOVIA_GROUP_LABEL = "LINHAS OFF-SITE/TUBOVIA";
/** Equipamento fora de operação: parado ou desativado no cadastro. */
const OUT_OF_SERVICE_EQUIPMENT_STATUSES = new Set(["OUT_OF_SERVICE", "INACTIVE"]);
/**
 * Relatório de Integridade EMITIDO: a partir da assinatura/conclusão. Rascunho
 * e relatório em análise ainda não foram emitidos.
 */
const ISSUED_REPORT_STATUSES = new Set(["SIGNED", "COMPLETED", "ISSUED"]);
/**
 * RECOMENDAÇÃO ABERTA. A Recommendation não possui campo de situação própria
 * no modelo (ver domain/recommendation/recommendation-domain.ts): ela nasce e
 * vive dentro do seu Relatório de Integridade. Portanto "aberta" é a
 * recomendação cujo RI ainda não foi concluído (assinado/emitido) — nada é
 * inventado aqui, e no dia em que a Recommendation ganhar situação própria
 * este é o único ponto a mudar.
 */
const CLOSED_RECOMMENDATION_REPORT_STATUSES = ISSUED_REPORT_STATUSES;

const MONTHS_PT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

const text = (value: unknown): string => (value === null || value === undefined ? "" : String(value));
const sameId = (a: unknown, b: unknown): boolean => text(a) !== "" && text(a) === text(b);

/** Data de referência da inspeção para o recorte de período e para a Curva S. */
function inspectionDateOf(inspection: DashboardInspection): string {
  return text(inspection.startDate) || text(inspection.endDate) || text(inspection.updatedAt);
}
function completionDateOf(inspection: DashboardInspection): string {
  return text(inspection.endDate) || text(inspection.updatedAt) || text(inspection.startDate);
}

export function isCompletedInspection(inspection: DashboardInspection): boolean {
  return COMPLETED_INSPECTION_STATUSES.has(text(inspection.status).toUpperCase());
}
export function isOutOfServiceEquipment(equipment: DashboardEquipment): boolean {
  return OUT_OF_SERVICE_EQUIPMENT_STATUSES.has(text(equipment.status).toUpperCase());
}
export function isIssuedReport(report: DashboardReport): boolean {
  return ISSUED_REPORT_STATUSES.has(text(report.status).toUpperCase());
}

/** O dia (AAAA-MM-DD) de um instante ISO. Exportado: o ritmo diário agrupa por ele. */
export const dayOf = (value: string): string => text(value).slice(0, 10);

export function withinPeriod(date: string, filters: DashboardFilters): boolean {
  const day = dayOf(date);
  if (!day) return !filters.from && !filters.to;
  if (filters.from && day < dayOf(filters.from)) return false;
  if (filters.to && day > dayOf(filters.to)) return false;
  return true;
}

/** Percentual inteiro 0–100, sempre coerente (nunca NaN, nunca > 100). */
export function percentOf(part: number, total: number): number {
  if (!total || total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((part / total) * 100)));
}

/** O critério oficial de avanço, um só para toda a tela. */
export function progressOf(inspections: readonly DashboardInspection[]): number {
  return percentOf(inspections.filter(isCompletedInspection).length, inspections.length);
}

/** Linha que entra no cálculo: a que não está Fora de Operação. */
export function isOperationalLine(item: DashboardFieldProgress): boolean {
  return !item.outOfOperation;
}

/**
 * Avanço por apontamento de campo: média dos percentuais das Linhas
 * OPERACIONAIS. Mantém uma casa decimal porque o número real da operação
 * costuma cair no meio (4 de 8 Linhas a 100% e 2 a 50% dá 62,5%, não 63%).
 */
export function fieldProgressOf(items: readonly DashboardFieldProgress[]): number {
  const operational = items.filter(isOperationalLine);
  if (operational.length === 0) return 0;
  const sum = operational.reduce((total, item) => total + item.progressPercent, 0);
  return Math.round((sum / operational.length) * 10) / 10;
}

function matchesScope(item: { clientId?: string | null; projectId?: string | null; unitAreaId?: string | null }, filters: DashboardFilters): boolean {
  if (filters.clientId && !sameId(item.clientId, filters.clientId)) return false;
  if (filters.projectId && !sameId(item.projectId, filters.projectId)) return false;
  if (filters.unitAreaId && !sameId(item.unitAreaId, filters.unitAreaId)) return false;
  return true;
}

/**
 * Sistemas de Tubulação ATRIBUÍDOS a um Inspetor, dentro do recorte.
 *
 * Só vínculo ATIVO conta: o desativado é histórico, não responsabilidade
 * atual. O escopo individual sai daqui e nunca de `updatedBy` — o avanço
 * pertence à Linha e é compartilhado por todos os Inspetores do Sistema
 * (`field-inspection-progress.ts`), de modo que "quem salvou por último"
 * não mede o trabalho de ninguém.
 */
export function systemsAssignedTo(
  professionalId: string,
  assignments: readonly DashboardAssignment[],
  filters: DashboardFilters = {}
): Set<string> {
  const systems = new Set<string>();
  for (const assignment of assignments) {
    if (!assignment.active) continue;
    if (!sameId(assignment.inspectorProfessionalId, professionalId)) continue;
    if (!matchesScope(assignment, filters)) continue;
    const systemId = text(assignment.systemId);
    if (systemId) systems.add(systemId);
  }
  return systems;
}

/** Linhas atribuídas diretamente quando o target não é um Sistema. */
export function linesAssignedTo(
  professionalId: string,
  assignments: readonly DashboardAssignment[],
  filters: DashboardFilters = {}
): Set<string> {
  const lines = new Set<string>();
  for (const assignment of assignments) {
    if (!assignment.active || assignment.targetType !== "LINE") continue;
    if (!sameId(assignment.inspectorProfessionalId, professionalId)) continue;
    if (!matchesScope(assignment, filters)) continue;
    const lineId = text(assignment.targetId);
    if (lineId) lines.add(lineId);
  }
  return lines;
}

/** Percentuais que fecham exatamente em 100% (o resto vai para a maior fatia). */
function closedPercentages(counts: readonly number[], total: number): number[] {
  if (total <= 0) return counts.map(() => 0);
  const raw = counts.map(c => (c / total) * 100);
  const floored = raw.map(v => Math.floor(v));
  let remainder = 100 - floored.reduce((a, b) => a + b, 0);
  const order = raw
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);
  const result = [...floored];
  let i = 0;
  while (remainder > 0 && order.length > 0) {
    const target = order[i % order.length]!;
    result[target.index] = (result[target.index] ?? 0) + 1;
    remainder -= 1;
    i += 1;
  }
  return result;
}

function monthKeysOf(dates: readonly string[]): string[] {
  const keys = new Set<string>();
  for (const date of dates) {
    const day = dayOf(date);
    if (day.length >= 7) keys.add(day.slice(0, 7));
  }
  return [...keys].sort();
}

function monthLabel(key: string): string {
  const month = Number(key.slice(5, 7));
  const name = MONTHS_PT[month - 1] || key;
  return name;
}

/**
 * CURVA S. Planejado = inspeções cuja data planejada (início) já chegou, de
 * forma acumulada; Real = inspeções efetivamente concluídas, acumuladas;
 * barras = concluídas no mês. Tudo vem das inspeções reais do recorte — se
 * não houver inspeção com data, não há curva (empty state).
 */
function buildCurve(inspections: readonly DashboardInspection[]): { hasData: boolean; points: CurvePoint[] } {
  const dated = inspections.filter(i => dayOf(inspectionDateOf(i)).length >= 7);
  if (dated.length === 0) return { hasData: false, points: [] };

  const months = monthKeysOf([
    ...dated.map(inspectionDateOf),
    ...dated.filter(isCompletedInspection).map(completionDateOf)
  ]);
  if (months.length === 0) return { hasData: false, points: [] };

  const total = dated.length;
  const points: CurvePoint[] = months.map(key => {
    const plannedCount = dated.filter(i => dayOf(inspectionDateOf(i)).slice(0, 7) <= key).length;
    const completedUntil = dated.filter(i => isCompletedInspection(i) && dayOf(completionDateOf(i)).slice(0, 7) <= key).length;
    const executedInMonth = dated.filter(i => isCompletedInspection(i) && dayOf(completionDateOf(i)).slice(0, 7) === key).length;
    return { key, label: monthLabel(key), planned: percentOf(plannedCount, total), real: percentOf(completedUntil, total), executed: executedInMonth };
  });
  return { hasData: true, points };
}

function buildBars(
  groups: ReadonlyMap<string, { label: string; inspections: DashboardInspection[] }>
): { hasData: boolean; bars: ProgressBar[] } {
  const bars: ProgressBar[] = [...groups.entries()]
    .filter(([, group]) => group.inspections.length > 0)
    .map(([id, group]) => ({
      id,
      label: group.label,
      percent: progressOf(group.inspections),
      completed: group.inspections.filter(isCompletedInspection).length,
      total: group.inspections.length
    }))
    .sort((a, b) => b.percent - a.percent || a.label.localeCompare(b.label, "pt-BR"));
  return { hasData: bars.length > 0, bars };
}

export function computeDashboard(input: DashboardInput, filters: DashboardFilters = {}): DashboardIndicators {
  // CORRETIVO P0: proteger contra coleções undefined em cenários de falha parcial do carregamento
  const safeInput: DashboardInput = {
    ...input,
    equipments: input.equipments ?? [],
    inspections: input.inspections ?? [],
    reports: input.reports ?? [],
    recommendations: input.recommendations ?? [],
    unitAreas: input.unitAreas ?? []
  };
  const equipments = safeInput.equipments.filter(e => matchesScope(e, filters));
  const equipmentIds = new Set(equipments.map(e => text(e.id)));

  /**
   * RECORTE POR INSPETOR. As duas fontes do escopo individual:
   *   · inspeções em que a pessoa é a responsável (`inspectorProfessionalId`);
   *   · Linhas dos Sistemas atribuídos a ela no Controle de Campo.
   * Sem `assignments`, o recorte de campo simplesmente não se aplica — e as
   * Linhas ficam de fora em vez de aparecerem como se fossem dela.
   */
  const inspectorId = text(filters.inspectorProfessionalId);
  const inspectorSystems = inspectorId ? systemsAssignedTo(inspectorId, input.assignments ?? [], filters) : null;
  // REGRA V4-CAUSA3: linhas diretas atribuídas ao inspetor (targetType="LINE")
  // devem ser incluídas no recorte mesmo quando systemId=null.
  const inspectorDirectLines = inspectorId ? linesAssignedTo(inspectorId, input.assignments ?? [], filters) : null;

  const inspections = input.inspections.filter(i => {
    if (inspectorId && !sameId(i.inspectorProfessionalId, inspectorId)) return false;
    // REGRESSÃO V3-A6: clientId sozinho DEVE restringir inspeções.
    // Se a inspeção tem equipmentId, o vínculo com Equipment é a fonte
    // preferencial (preserva legado sem projectId). Se não tem, usa
    // matchesScope direto (que agora inclui clientId).
    const hasStructuralScope = filters.clientId || filters.projectId || filters.unitAreaId;
    const scoped = hasStructuralScope
      ? (i.equipmentId
          ? equipmentIds.has(text(i.equipmentId))
          : matchesScope(i, filters))
      : true;
    return scoped && withinPeriod(inspectionDateOf(i), filters);
  });
  const inspectionIds = new Set(inspections.map(i => text(i.id)));

  // CONTROLE DE CAMPO no mesmo recorte do Painel (item 38): Projeto e
  // Unidade/Área filtram o apontamento exatamente como filtram o resto; o
  // Inspetor filtra pelos Sistemas atribuídos a ele.
  // REGRA V4-CAUSA3: fieldProgress deve respeitar tanto sistemas quanto linhas
// diretas atribuídas ao inspetor. Linhas OFF-SITE (systemId=null) entram
// via inspectorDirectLines quando targetType="LINE".
const fieldProgress = (input.fieldProgress ?? [])
    .filter(item => matchesScope(item, filters))
    .filter(item => {
      if (!inspectorSystems && !inspectorDirectLines) return true;
      const sysId = text(item.systemId);
      const lineId = text(item.lineEquipmentId);
      const inSystem = inspectorSystems ? inspectorSystems.has(sysId) : false;
      const inDirectLine = inspectorDirectLines ? inspectorDirectLines.has(lineId) : false;
      return inSystem || inDirectLine;
    });
  const hasFieldProgress = fieldProgress.length > 0;

  /**
   * Ativos do recorte INDIVIDUAL: os que a pessoa inspecionou mais os
   * Sistemas atribuídos a ela e suas Linhas. Sem isso, escolher um Inspetor
   * mudaria as inspeções mas manteria o card de ATIVOS com a planta inteira.
   */
  const individualEquipmentIds = inspectorId
    ? new Set<string>([
        ...inspections.map(i => text(i.equipmentId)).filter(id => id !== ""),
        ...fieldProgress.map(item => text(item.lineEquipmentId)).filter(id => id !== ""),
        ...(inspectorSystems ?? [])
      ])
    : null;
  const scopedEquipments = individualEquipmentIds
    ? equipments.filter(e => individualEquipmentIds.has(text(e.id)))
    : equipments;

  // REGRESSÃO V3-A6/A7: relatórios devem respeitar clientId mesmo sem inspectionId.
  // Se tem inspectionId → entra somente se a inspeção está no escopo filtrado.
  // Se NÃO tem inspectionId → aplica matchesScope direto (que inclui clientId).
  const reports = safeInput.reports.filter(r => {
    if (r.inspectionId) return inspectionIds.has(text(r.inspectionId));
    // Relatório órfão (sem inspectionId): filtra por clientId/projeto/unidade/período
    return matchesScope(r, filters) && withinPeriod(text(r.updatedAt), filters);
  });
  const issuedReports = reports.filter(isIssuedReport);

  const reportById = new Map(safeInput.reports.map(r => [text(r.id), r]));
  const reportIdsInScope = new Set(reports.map(r => text(r.id)));
  // REGRESSÃO V3-A6/A8: recomendações devem respeitar clientId mesmo sem equipmentId.
  // Se tem equipmentId → vínculo com Equipment é preferencial (preserva legado).
  // Se NÃO tem equipmentId → usa matchesScope direto (que inclui clientId).
  const recommendations = input.recommendations.filter(rec => {
    const hasStructuralScope = filters.clientId || filters.projectId || filters.unitAreaId;
    if (hasStructuralScope) {
      const scoped = rec.equipmentId
        ? equipmentIds.has(text(rec.equipmentId))
        : matchesScope(rec, filters);
      if (!scoped) return false;
    }
    // Com Inspetor selecionado, a recomendação só é dele se nasceu de um RI
    // do recorte ou recai sobre um ativo do escopo individual.
    if (individualEquipmentIds) {
      const fromOwnReport = rec.integrityReportId ? reportIdsInScope.has(text(rec.integrityReportId)) : false;
      if (!fromOwnReport && !individualEquipmentIds.has(text(rec.equipmentId))) return false;
    }
    return withinPeriod(text(rec.createdAt), filters);
  });
  const openRecommendations = recommendations.filter(rec => {
    const report = rec.integrityReportId ? reportById.get(text(rec.integrityReportId)) : undefined;
    if (!report) return true; // sem RI concluído associado, a recomendação segue aberta
    return !CLOSED_RECOMMENDATION_REPORT_STATUSES.has(text(report.status).toUpperCase());
  });

  const curve = buildCurve(inspections);
  const realProgress = hasFieldProgress ? fieldProgressOf(fieldProgress) : progressOf(inspections);
  // Planejado = o ponto atual da mesma curva (nunca uma segunda conta).
  const plannedProgress = curve.hasData ? (curve.points[curve.points.length - 1]?.planned ?? 0) : 0;

  // STATUS DAS INSPEÇÕES — universo = ativos do recorte, em três categorias oficiais.
  const completedEquipmentIds = new Set(
    inspections.filter(isCompletedInspection).map(i => text(i.equipmentId)).filter(id => id !== "")
  );
  const outOfService = scopedEquipments.filter(isOutOfServiceEquipment);
  const operational = scopedEquipments.filter(e => !isOutOfServiceEquipment(e));
  const concluded = operational.filter(e => completedEquipmentIds.has(text(e.id)));
  const pending = operational.filter(e => !completedEquipmentIds.has(text(e.id)));
  // Com apontamento de campo, as três categorias são contadas por LINHA:
  // CONCLUÍDA é 100% e operacional; PENDENTE é operacional de 0 a 99%.
  const statusCounts = hasFieldProgress
    ? [
        fieldProgress.filter(item => isOperationalLine(item) && item.progressPercent >= 100).length,
        fieldProgress.filter(item => isOperationalLine(item) && item.progressPercent < 100).length,
        fieldProgress.filter(item => !isOperationalLine(item)).length
      ]
    : [concluded.length, pending.length, outOfService.length];
  const statusTotal = hasFieldProgress ? fieldProgress.length : scopedEquipments.length;
  const statusPercents = closedPercentages(statusCounts, statusTotal);
  const status = {
    hasData: statusTotal > 0,
    total: statusTotal,
    slices: [
      { key: "CONCLUIDAS" as const, label: "Concluídas", count: statusCounts[0]!, percent: statusPercents[0]! },
      { key: "PENDENTES" as const, label: "Pendentes", count: statusCounts[1]!, percent: statusPercents[1]! },
      { key: "FORA_DE_OPERACAO" as const, label: "Fora de operação", count: statusCounts[2]!, percent: statusPercents[2]! }
    ]
  };

  // AVANÇOS POR UNIDADE — pelas Unidades/Áreas reais do recorte.
  const equipmentById = new Map(scopedEquipments.map(e => [text(e.id), e]));
  const unitGroups = new Map<string, { label: string; inspections: DashboardInspection[] }>();
  const unitLabel = (unit: DashboardUnitArea): string => [text(unit.code), text(unit.name)].filter(Boolean).join(" - ") || text(unit.id);
  for (const unit of input.unitAreas) {
    // REGRESSÃO V3-A9: clientId deve filtrar unidades explicitamente.
    // DashboardUnitArea usa unit.id como identidade, não unitAreaId.
    if (filters.clientId && !sameId(unit.clientId, filters.clientId)) continue;
    if (filters.projectId && !sameId(unit.projectId, filters.projectId)) continue;
    if (filters.unitAreaId && !sameId(unit.id, filters.unitAreaId)) continue;
    unitGroups.set(text(unit.id), { label: unitLabel(unit), inspections: [] });
  }
  for (const inspection of inspections) {
    const unitId = text(inspection.unitAreaId) || text(equipmentById.get(text(inspection.equipmentId))?.unitAreaId);
    if (!unitId) continue;
    const group = unitGroups.get(unitId);
    if (group) group.inspections.push(inspection);
  }

  // AVANÇO POR SISTEMAS — pelo campo `system` do equipamento (cadastro real).
  const systemGroups = new Map<string, { label: string; inspections: DashboardInspection[] }>();
  for (const inspection of inspections) {
    const equipment = equipmentById.get(text(inspection.equipmentId));
    const system = text(equipment?.system).trim();
    if (!system) continue;
    const key = system.toUpperCase();
    if (!systemGroups.has(key)) systemGroups.set(key, { label: system, inspections: [] });
    systemGroups.get(key)!.inspections.push(inspection);
  }

  // AVANÇOS POR UNIDADE / POR SISTEMA vêm do apontamento de campo quando ele
  // existe — a mesma fonte do card, nunca uma mistura das duas contas.
  const systemTagOf = (systemId: string): string => text(equipmentById.get(systemId)?.tag) || systemId;
  const unitLabelById = new Map<string, string>();
  for (const [id, group] of unitGroups) unitLabelById.set(id, group.label);

  return {
    assets: scopedEquipments.length,
    completedInspections: inspections.filter(isCompletedInspection).length,
    totalInspections: inspections.length,
    plannedProgress,
    realProgress,
    deviation: realProgress - plannedProgress,
    issuedReports: issuedReports.length,
    openRecommendations: openRecommendations.length,
    curve,
    status,
    unitProgress: hasFieldProgress
      ? buildFieldBars(fieldProgress, item => text(item.unitAreaId), id => unitLabelById.get(id) ?? id)
      : buildBars(unitGroups),
    // REGRA V4-CAUSA2: Linhas sem systemId são agrupadas sob OFFSITE_TUBOVIA_GROUP_ID
    // em vez de serem descartadas pelo filtro `if (!key) continue` em buildFieldBars.
    systemProgress: hasFieldProgress
      ? buildFieldBars(
          fieldProgress,
          item => text(item.systemId) || OFFSITE_TUBOVIA_GROUP_ID,
          key => key === OFFSITE_TUBOVIA_GROUP_ID ? OFFSITE_TUBOVIA_GROUP_LABEL : systemTagOf(key)
        )
      : buildBars(systemGroups)
  };
}

/**
 * Barras a partir do apontamento de campo: percentual é a média das Linhas
 * operacionais do grupo; `completed`/`total` contam Linhas (não inspeções),
 * mantendo o mesmo formato visual das barras históricas.
 */
function buildFieldBars(
  items: readonly DashboardFieldProgress[],
  keyOf: (item: DashboardFieldProgress) => string,
  labelOf: (key: string) => string
): { hasData: boolean; bars: ProgressBar[] } {
  const groups = new Map<string, DashboardFieldProgress[]>();
  for (const item of items) {
    const key = keyOf(item);
    if (!key) continue;
    const bucket = groups.get(key);
    if (bucket) bucket.push(item);
    else groups.set(key, [item]);
  }
  const bars: ProgressBar[] = [...groups.entries()].map(([key, group]) => ({
    id: key,
    label: labelOf(key),
    percent: Math.round(fieldProgressOf(group)),
    completed: group.filter(item => isOperationalLine(item) && item.progressPercent >= 100).length,
    total: group.filter(isOperationalLine).length
  }));
  bars.sort((a, b) => b.percent - a.percent || a.label.localeCompare(b.label, "pt-BR"));
  return { hasData: bars.length > 0, bars };
}
